/**
 * Seam boundary for the rules data source — WIRED to the API (ENG-180 W5).
 * The generated Apollo query drives `rules`; create/update/delete map the
 * webapp `Rule` model to the GraphQL inputs and refetch the list. Consumers only
 * import `usePermissionRules`, so nothing else changed when this swapped from the
 * stub.
 */

import { notifications } from "@mantine/notifications";
import { useCallback, useMemo } from "react";
import {
	useCreatePermissionRuleMutation,
	useDeletePermissionRuleMutation,
	usePermissionRulesQuery,
	useUpdatePermissionRuleMutation,
} from "../../graphql/generated/types.gen";
import { useActiveOrganization } from "../../hooks/useActiveOrganization";
import type { Rule } from "../types";
import { gqlToRule, patchToUpdateInput, ruleToCreateInput } from "./mappers";
import type { RulesSeam } from "./types";

// EffectiveNodeAccess is the server-authoritative preview-as map; refetch it so an
// override set while previewing reflects immediately (skipped when not previewing,
// so it's inert for the real viewer).
const REFETCH = ["PermissionRules", "EffectiveNodeAccess"];

// Surface a server rejection instead of the modal silently reporting success.
// Providing onError also lets Apollo resolve (not reject) the mutation promise.
const onMutationError = (action: string) => (error: unknown) =>
	notifications.show({
		title: `Couldn't ${action} rule`,
		message: error instanceof Error ? error.message : "Please try again.",
		color: "red",
	});

export function usePermissionRules(): RulesSeam {
	const { activeOrganizationId } = useActiveOrganization();
	const organizationId = activeOrganizationId ?? "";
	const skip = organizationId === "";

	const { data, loading } = usePermissionRulesQuery({
		variables: { organizationId },
		skip,
	});
	const [createMutation] = useCreatePermissionRuleMutation();
	const [updateMutation] = useUpdatePermissionRuleMutation();
	const [deleteMutation] = useDeletePermissionRuleMutation();

	const rules = useMemo(
		() =>
			(data?.permissionRules ?? [])
				.map(gqlToRule)
				.filter((rule): rule is Rule => rule !== null),
		[data],
	);

	const createRule = useCallback(
		(input: Omit<Rule, "id">) => {
			if (skip) return;
			void createMutation({
				variables: { organizationId, input: ruleToCreateInput(input) },
				refetchQueries: REFETCH,
				onError: onMutationError("create"),
			});
		},
		[skip, organizationId, createMutation],
	);

	const updateRule = useCallback(
		(id: string, patch: Partial<Rule>) => {
			if (skip) return;
			void updateMutation({
				variables: { organizationId, id, input: patchToUpdateInput(patch) },
				refetchQueries: REFETCH,
				onError: onMutationError("update"),
			});
		},
		[skip, organizationId, updateMutation],
	);

	const deleteRule = useCallback(
		(id: string) => {
			if (skip) return;
			void deleteMutation({
				variables: { organizationId, id },
				refetchQueries: REFETCH,
				onError: onMutationError("delete"),
			});
		},
		[skip, organizationId, deleteMutation],
	);

	return { rules, loading, createRule, updateRule, deleteRule };
}
